import { useFieldArray, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { DatasetTypeIcon } from "@/components/dataset/datasetList/DatasetTypeIcon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import Checkbox from "@/components/ui/checkbox";
import FormField from "@/components/ui/formField";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { EOutputAnalysisType } from "@/types/spatial";
import { formatStorageFromKB } from "@/utils/format";

import { twMerge } from "tailwind-merge";

import { DatasetEntryFormData } from ".";

interface DatasetAccordionProps {
  expandedItem: string | null;
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  setExpandedItem: (id: string | null) => void;
}

// Func to get dataset name before and after change
const getDatasetName = (name: string, newName: string = "") => {
  if (!newName.trim()) return <p className="truncate">{name}</p>;

  return (
    <p className="overflow-hidden flex flex-row items-center">
      <span className="w-[150px] overflow-hidden truncate">{name}</span>
      <span className="m-2">→</span>
      <span className="w-[150px] overflow-hidden text-left truncate">
        {newName}
      </span>
    </p>
  );
};

const DatasetAccordion = ({
  expandedItem,
  selectedItems,
  setExpandedItem,
  setSelectedItems
}: DatasetAccordionProps) => {
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<DatasetEntryFormData>();

  const { latestSaveAsDataset } = useSelector(
    (state: RootState) => state.spatial
  );

  const { fields } = useFieldArray({
    control,
    name: "datasetsInfo"
  });

  // Check if dataset has been previously saved
  const getSavedDatasetName = (datasetId: string) => {
    if (!latestSaveAsDataset?.datasetsInfo?.length) return null;
    return (
      latestSaveAsDataset?.datasetsInfo?.find(
        (item) => item.datasetId === datasetId
      )?.datasetName ?? null
    );
  };

  // Handler to select or deselect an item
  const handleSelectItem = (id: string) => {
    setSelectedItems(
      selectedItems.includes(id)
        ? selectedItems.filter((item) => item !== id)
        : [...selectedItems, id]
    );
  };

  const handleExpandItem = (id: string) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full flex flex-col gap-2 mt-3"
    >
      {fields.map((dataset, index) => {
        const { datasetId, spatialType, name, id, datasetSize } = dataset;
        const isExpanded = expandedItem === id;
        const isSelected = selectedItems.includes(datasetId);
        const savedName = getSavedDatasetName(datasetId);

        const datasetNameValue = getValues(`datasetsInfo.${index}.datasetName`);

        return (
          <div key={id}>
            <AccordionItem
              className={twMerge(
                "w-full relative flex flex-col border border-gainsboro rounded-xl overflow-hidden",
                errors?.datasetsInfo?.[index]?.datasetName && "border-cinnabar"
              )}
              value={id}
              data-testid="accordion-item"
              disabled={!!savedName}
            >
              <AccordionTrigger
                className={twMerge(
                  "px-2 py-1 flex justify-between h-[38px]",
                  isExpanded && "bg-whisper",
                  savedName && "bg-white-smoke"
                )}
                data-testid={`accordion-trigger-${datasetId}`}
                onClick={() => handleExpandItem(id)}
              >
                <div className="flex gap-2 items-center w-full mr-2">
                  <Checkbox
                    checked={isSelected || !!savedName}
                    className="w-4"
                    onChange={() => handleSelectItem(datasetId)}
                    data-testid={`checkbox-${datasetId}`}
                    disabled={!!savedName}
                  />
                  {name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME ? (
                    <DatasetTypeIcon type={EOutputAnalysisType.GRID} /> // Forces the icon to be the grid icon for spatial analysis results dataset
                  ) : (
                    <DatasetTypeIcon type={spatialType} />
                  )}
                  <div
                    className={twMerge(
                      "flex flex-row flex-nowrap text-txtHeading text-xs max-w-[256px] gap-2",
                      isExpanded && "font-bold",
                      savedName && "text-light-gray"
                    )}
                  >
                    {getDatasetName(name, datasetNameValue)}
                  </div>

                  <p
                    className={cn(
                      "ml-auto text-txtHeading text-xs text-nowrap",
                      isSelected && "font-semibold"
                    )}
                  >
                    {formatStorageFromKB(datasetSize)}
                  </p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-t border-gainsboro">
                <div className="flex flex-col gap-2 mx-2 mt-3">
                  <FormField
                    name={`datasetsInfo.${index}.datasetName`}
                    register={register}
                    rules={{
                      validate: (value) => {
                        if (!isSelected) return true;
                        return !!value || "Dataset name is required";
                      }
                    }}
                    placeHolder="Dataset name"
                    resetValue={() =>
                      setValue(`datasetsInfo.${index}.datasetName`, "")
                    }
                  />
                  <FormField
                    name={`datasetsInfo.${index}.description`}
                    isAreaInput
                    register={register}
                    rules={{
                      validate: (value) => {
                        if (!isSelected) return true;
                        return !!value || "Description is required";
                      }
                    }}
                    placeHolder="Description"
                    resetValue={() =>
                      setValue(`datasetsInfo.${index}.description`, "")
                    }
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
            {errors?.datasetsInfo?.[index]?.datasetName && (
              <span className="text-[10px] text-cinnabar pl-2">
                Name already exists
              </span>
            )}
            {savedName && (
              <span className="text-[10px] text-txtHeading pl-2">
                Dataset has been saved, with data name “{savedName}”
              </span>
            )}
          </div>
        );
      })}
    </Accordion>
  );
};

export default DatasetAccordion;
